import { RefreshCw } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';

// Shared presentational helpers for the Event Ops admin surface.

// Manual refresh button — data also auto-refreshes every 10s, this is for
// "I want it now". Spins while a fetch (background or manual) is in flight.
export function RefreshButton({
  onClick,
  isFetching,
}: {
  onClick: () => void;
  isFetching: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={isFetching}
      title="Refresh now (auto-refreshes every 10s)"
      className="inline-flex items-center justify-center p-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-dark-muted hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-60"
    >
      <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
    </button>
  );
}

export function healthBgClass(health: AdminAPI.OpsHealth): string {
  return health === 'red' ? 'bg-red-500' : health === 'yellow' ? 'bg-amber-400' : 'bg-emerald-500';
}

export function HealthDot({ health, className = '' }: { health: AdminAPI.OpsHealth; className?: string }) {
  return (
    <span className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${healthBgClass(health)} ${className}`} />
  );
}

export const RUN_STATUS_STYLES: Record<string, string> = {
  queued: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
  running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  cancelled: 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
};

export const MILESTONE_STATUS_STYLES: Record<string, string> = {
  pending: 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 animate-pulse',
  done: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  blocked: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  skipped: 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500 line-through',
};

export function StatusChip({ status, styles }: { status: string; styles: Record<string, string> }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status] ?? styles.pending ?? ''}`}
    >
      {status}
    </span>
  );
}

export function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function timeUntil(iso: string | null): string {
  if (!iso) return '—';
  const diffMs = new Date(iso).getTime() - Date.now();
  const mins = Math.round(diffMs / 60000);
  if (mins <= 0) return 'now';
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
}

export function formatLocalDinner(tile: Pick<AdminAPI.OpsTile, 'localDinnerAt' | 'timezone'>): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tile.timezone,
      timeZoneName: 'short',
    }).format(new Date(tile.localDinnerAt));
  } catch {
    return tile.localDinnerAt;
  }
}

// "2026-07-30_19:00" → "Jul 30 · 19:00" (falls back to the raw id).
export function eventLabel(eventTimeId: string): string {
  const m = eventTimeId.match(/^(\d{4}-\d{2}-\d{2})_(\d{2}:\d{2})$/);
  if (!m) return eventTimeId;
  try {
    const day = new Date(`${m[1]}T12:00:00`).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return `${day} · ${m[2]}`;
  } catch {
    return eventTimeId;
  }
}

/** e.g. [6,6,5] → "6+6+5" */
export function tableSizeList(sizes: number[]): string {
  return sizes.length ? sizes.join('+') : '—';
}

// Pipeline stages, in execution order (mirrors the server's milestone rail).
export const STAGE_SHORT: Record<string, string> = {
  'match.initial': 'Match',
  'match.reoptimize': 'Re-opt',
  'reserve.book': 'Reserve',
  'match.finalize': 'Check',
  lock_notify: 'Lock',
  'reserve.verify': 'Audit',
  'reserve.release': 'Release',
};

// The AI pipeline runs — one pip/button each. `lock_notify` is a system step
// (existing T-24h handler), shown on the detail page's rail but not part of
// the AI pipeline.
export const AI_STAGE_TASKS: AdminAPI.AgentTaskType[] = [
  'match.initial',
  'match.reoptimize',
  'reserve.book',
  'match.finalize',
  'reserve.verify',
  'reserve.release',
];

// Stages that run once per EVENT (not per neighborhood) — shown at the event
// level on the overview instead of inside every neighborhood tile.
export const EVENT_SCOPED_STAGES = new Set<string>(AdminAPI.EVENT_SCOPED_AGENT_TASK_TYPES);

// The stages shown inside individual neighborhood views (tile pips, stage-run
// buttons). Booking is here because it is per-neighborhood: it fires at T-48h
// of that neighborhood's own local dinner. The finalize pre-flight "Check" and
// the event-wide audit are deliberately absent: Check is runnable from the
// event-level bulk control, the audit from the event header chip.
export const NEIGHBORHOOD_STAGE_TASKS: AdminAPI.AgentTaskType[] = [
  'match.initial',
  'match.reoptimize',
  'reserve.book',
];

export const PIP_STYLES: Record<string, string> = {
  done: 'bg-emerald-500',
  running: 'bg-blue-500 animate-pulse',
  scheduled: 'bg-blue-300 dark:bg-blue-400',
  blocked: 'bg-red-500',
  // Window passed without the run ever executing — that's an attention state,
  // not a neutral one (every stage should run at least once).
  skipped: 'border border-amber-400 dark:border-amber-500 bg-amber-200/60 dark:bg-amber-900/40',
  pending: 'border border-neutral-300 dark:border-neutral-600 bg-transparent',
};

export function stageStatusLabel(status: AdminAPI.OpsMilestone['status']): string {
  switch (status) {
    case 'done':
      return 'ran';
    case 'running':
      return 'running now';
    case 'scheduled':
      return 'queued';
    case 'blocked':
      return 'failed (max attempts)';
    case 'skipped':
      return 'never ran (window passed)';
    default:
      return 'not yet due';
  }
}

function neighborhoodStages(milestones: AdminAPI.OpsMilestone[]): AdminAPI.OpsMilestone[] {
  return milestones.filter((m) => (NEIGHBORHOOD_STAGE_TASKS as string[]).includes(m.task));
}

// One pip per neighborhood-level AI pipeline run: green = ran, pulsing blue =
// running, amber = window passed without a run, hollow = not yet due.
// Event-level stages (finalize check, audit) are not repeated per tile —
// they live in the event header (bulk control / EventStageChips).
export function StagePips({ milestones }: { milestones: AdminAPI.OpsMilestone[] }) {
  return (
    <span className="inline-flex items-center gap-[3px] shrink-0">
      {neighborhoodStages(milestones)
        .map((m) => (
        <span
          key={m.task}
          title={`${STAGE_SHORT[m.task] ?? m.task} — ${m.label}: ${stageStatusLabel(m.status)} (${
            new Date(m.plannedAt) > new Date() ? timeUntil(m.plannedAt) : timeAgo(m.plannedAt)
          })`}
          className={`w-2 h-2 rounded-[3px] ${PIP_STYLES[m.status] ?? PIP_STYLES.pending}`}
        />
      ))}
    </span>
  );
}

export const TASK_LABELS: Record<string, string> = {
  'match.initial': 'Initial matching',
  'match.reoptimize': 'Re-optimize',
  'match.finalize': 'Finalize check',
  'reserve.book': 'Book reservations',
  'reserve.verify': 'Verify reservations',
  'reserve.release': 'Release reservations',
  'venue.research': 'Venue research',
};
